/**
 * 권한 그룹 목록 페이지
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, Badge, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/layout/AdminLayout';
import DataTable, { Column } from '../../components/common/DataTable';
import Pagination from '../../components/common/Pagination';
import FilterBar, { FilterField } from '../../components/common/FilterBar';
import { roleGroupService } from '../../services/roleGroupService';
import { RoleGroup, RoleGroupFilter } from '../../types/admin';

const RoleGroupsList: React.FC = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<RoleGroupFilter>({
    page: 1,
    limit: 20,
  });

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['roleGroups', filter],
    queryFn: () => roleGroupService.getRoleGroups(filter),
  });

  const getStatusBadge = (status: string) => {
    return status === 'active' ? (
      <Badge bg="success">활성</Badge>
    ) : (
      <Badge bg="secondary">비활성</Badge>
    );
  };

  const columns: Column<RoleGroup>[] = [
    {
      key: 'id',
      label: 'ID',
      width: '80px',
    },
    {
      key: 'group_name',
      label: '그룹명',
      render: (roleGroup) => (
        <span
          style={{ cursor: 'pointer', color: '#00BCD4', fontWeight: 'bold' }}
          onClick={() => navigate(`/admin/admins/role-groups/${roleGroup.id}`)}
        >
          {roleGroup.group_name}
        </span>
      ),
    },
    {
      key: 'description',
      label: '설명',
      render: (roleGroup) => (
        <span style={{ color: '#ccc' }}>{roleGroup.description || '-'}</span>
      ),
    },
    {
      key: 'status',
      label: '상태',
      render: (roleGroup) => getStatusBadge(roleGroup.status),
    },
    {
      key: 'created_at',
      label: '등록일',
      render: (roleGroup) => (
        <span style={{ color: '#ccc' }}>
          {new Date(roleGroup.created_at).toLocaleDateString('ko-KR')}
        </span>
      ),
    },
  ];

  const filterFields: FilterField[] = [
    {
      name: 'search',
      label: '검색',
      type: 'text',
      placeholder: '그룹명으로 검색',
    },
    {
      name: 'status',
      label: '상태',
      type: 'select',
      options: [
        { value: '', label: '전체' },
        { value: 'active', label: '활성' },
        { value: 'inactive', label: '비활성' },
      ],
    },
  ];

  const handleFilterChange = (values: Record<string, any>) => {
    setFilter((prev) => ({
      ...prev,
      ...values,
      page: 1,
    }));
  };

  const handlePageChange = (page: number) => {
    setFilter((prev) => ({ ...prev, page }));
  };

  const handleRowClick = (roleGroup: RoleGroup) => {
    navigate(`/admin/admins/role-groups/${roleGroup.id}`);
  };

  return (
    <AdminLayout>
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 style={{ color: '#fff' }}>권한 그룹 관리</h1>
          <div>
            <Button variant="secondary" onClick={() => navigate('/admin/admins')} className="me-2">
              관리자 목록
            </Button>
            <Button variant="primary" onClick={() => navigate('/admin/admins/role-groups/new')}>
              권한 그룹 등록
            </Button>
          </div>
        </div>

        <Card className="mb-4">
          <Card.Body>
            <FilterBar
              fields={filterFields}
              values={filter}
              onChange={handleFilterChange}
              onSearch={() => refetch()}
              onReset={() => setFilter({ page: 1, limit: 20 })}
            />
          </Card.Body>
        </Card>

        <Card>
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                총 <strong>{data?.pagination.total_count || 0}</strong>건
              </div>
            </div>

            <DataTable
              columns={columns}
              data={data?.role_groups || []}
              loading={isLoading}
              emptyMessage="권한 그룹이 없습니다."
              onRowClick={handleRowClick}
            />

            {data && data.pagination.total_pages > 1 && (
              <Pagination
                currentPage={data.pagination.current_page}
                totalPages={data.pagination.total_pages}
                onPageChange={handlePageChange}
              />
            )}
          </Card.Body>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default RoleGroupsList;

